const TAP_PROJECT_VERSION = 'tap.project.v1';
const BANNER_ROLL_NOTE = 'At least one side must be 52 inches or less. The other can run along the roll.';
const UV_TRANSFER_NOTE = 'UV transfer stickers have a maximum print width of 11.5 inches.';
const GARMENT_SIZES = ['S', 'M', 'L', 'XL', 'XXL'];
const SHIRT_MATERIALS = [
  ['smart-blank-4oz', 'All-cotton Smart Blanks · 4 oz'],
  ['smart-blank-6oz', 'All-cotton Smart Blanks · 6 oz'],
  ['pro-club', 'Pro Club'],
  ['pro-five', 'ProFive'],
];
const HOODIE_MATERIALS = [['smart-blank-reserve-hoodie', 'Smart Blank Reserve hoodie']];
const APPAREL_PRINT_AREAS = {
  S: { width: 16, height: 20, visualScale: 0.88 },
  M: { width: 16, height: 20, visualScale: 0.94 },
  L: { width: 16, height: 20, visualScale: 1 },
  XL: { width: 16, height: 20, visualScale: 1.06 },
  XXL: { width: 16, height: 20, visualScale: 1.12 },
};
const roundDimension = (value) => Math.round(value * 100) / 100;

const PRODUCT_TYPES = {
  apparel: {
    label: 'Apparel',
    materials: [...SHIRT_MATERIALS, ...HOODIE_MATERIALS],
    methods: [
      ['dtg', 'DTG · direct to garment'],
      ['dtf', 'DTF · direct to film'],
    ],
    width: 11,
    height: 11,
    note: 'Garment size controls the fit. Standard adult front graphics are 11" × 11"; left-chest graphics are 4.5" × 4.5". XXL garments may look best with graphics 12"–14" wide.',
  },
  cup: {
    label: 'Cup',
    materials: [
      ['ceramic-mug', 'Ceramic mug'],
      ['stainless-tumbler', 'Stainless tumbler'],
      ['customer-supplied-cup', 'Customer-supplied cup'],
    ],
    methods: [
      ['sublimation', 'Sublimation'],
      ['uv-dtf', 'UV DTF transfer'],
    ],
    width: 9,
    height: 4,
    note: 'Enter the printable wrap area. We will confirm the cup size and safe margins.',
  },
  banner: {
    label: 'Banner',
    materials: [
      ['vinyl-13', '13 oz vinyl'],
      ['vinyl-18', '18 oz blockout vinyl'],
      ['mesh', 'Mesh banner'],
    ],
    methods: [['wide-format', 'Wide-format print']],
    width: 72,
    height: 36,
    note: '',
  },
  sticker: {
    label: 'Sticker',
    materials: [
      ['vinyl', 'Adhesive vinyl'],
      ['uv', 'UV transfer'],
      ['reflective', 'Reflective vinyl'],
      ['transparent', 'Clear vinyl'],
    ],
    methods: [
      ['kiss-cut', 'Kiss cut'],
      ['die-cut', 'Die cut'],
      ['sheet', 'Sheet'],
    ],
    width: 6,
    height: 4,
    note: 'Final cut path and bleed are confirmed during production review.',
  },
  poster: {
    label: 'Poster',
    materials: [
      ['poster-banner', 'Poster banner'],
      ['glossy-poster', 'Glossy poster'],
      ['matte-poster', 'Matte poster'],
    ],
    methods: [['wide-format', 'Wide-format print']],
    width: 24,
    height: 18,
    note: 'Enter the finished trim size.',
  },
  canopy: {
    label: 'Canopy',
    materials: [
      ['polyester', 'Printed polyester'],
      ['weatherproof', 'Weather-resistant polyester'],
    ],
    methods: [
      ['top-only', 'Canopy top'],
      ['top-walls', 'Top + walls'],
    ],
    width: 120,
    height: 120,
    note: 'Dimensions describe the canopy footprint. We will confirm printable panels.',
  },
  installation: {
    label: 'Installation',
    materials: [['recommend', 'Recommend a material']],
    methods: [['installation', 'Installation service']],
    width: '',
    height: '',
    note: 'Use notes for the surface, address, access, and preferred installation date.',
  },
  custom: {
    label: 'Custom',
    materials: [
      ['recommend', 'Recommend a material'],
      ['customer-supplied', 'Customer-supplied blank'],
    ],
    methods: [['recommend', 'Recommend a process']],
    width: '',
    height: '',
    note: 'Sketch the idea here; we will confirm the production method.',
  },
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const normalizeRotation = (value) => {
  const rotation = Number(value) || 0;
  const normalized = ((rotation % 360) + 360) % 360;
  return normalized > 180 ? normalized - 360 : normalized;
};
const createId = () =>
  globalThis.crypto?.randomUUID?.() || `tap-${Date.now()}-${Math.random().toString(16).slice(2)}`;

class TapStudio extends HTMLElement {
  constructor() {
    super();
    this.state = {
      mode: '',
      type: '',
      material: '',
      method: '',
      garmentSize: '',
      garmentStyle: '',
      expedited: false,
      rulerUnit: 'imperial',
      zoom: 1,
      objects: [],
      files: [],
      selectedObjectId: '',
      submissionId: createId(),
    };
    this.dragState = null;
    this.nativeNotificationReference = '';
    this.nativeNotificationArtwork = [];
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.apiEndpoint = this.dataset.apiEndpoint.trim();
    this.form = this.querySelector('form');
    this.cacheElements();
    this.bindEvents();
    this.addSheetRow();

    if (this.querySelector('[data-native-success]') || this.querySelector('.tap-form-errors')) {
      this.state.mode = sessionStorage.getItem('tap:last-mode') || 'interactive';
      this.showView('review', false);
      queueMicrotask(() => this.querySelector('[data-native-success]')?.focus());
    }
  }

  cacheElements() {
    this.views = [...this.querySelectorAll('[data-view]')];
    this.visualSteps = [...this.querySelectorAll('[data-visual-step]')];
    this.stepperButtons = [...this.querySelectorAll('[data-visual-goto]')];
    this.stage = this.querySelector('[data-stage]');
    this.surface = this.querySelector('.tap-design-surface');
    this.objectLayer = this.querySelector('[data-object-layer]');
    this.materialSelect = this.querySelector('[data-material]');
    this.methodSelect = this.querySelector('[data-method]');
    this.garmentSizeField = this.querySelector('[data-garment-size-field]');
    this.garmentSizeSelect = this.querySelector('[data-garment-size]');
    this.garmentStyleField = this.querySelector('[data-garment-style-field]');
    this.garmentStyleSelect = this.querySelector('[data-garment-style]');
    this.widthInput = this.querySelector('[data-width]');
    this.heightInput = this.querySelector('[data-height]');
    this.quantityInput = this.querySelector('[data-quantity]');
    this.resetPrintSizeButton = this.querySelector('[data-reset-print-size]');
    this.dimensionNote = this.querySelector('[data-dimension-note]');
    this.dimensionError = this.querySelector('[data-dimension-error]');
    this.artworkInput = this.querySelector('[data-artwork-input]');
    this.inspector = this.querySelector('[data-object-inspector]');
    this.layerList = this.querySelector('[data-layer-list]');
    this.layerMenu = this.querySelector('[data-layer-menu]');
    this.rulerX = this.querySelector('[data-ruler-x]');
    this.rulerY = this.querySelector('[data-ruler-y]');
    this.zoomOutput = this.querySelector('[data-zoom-output]');
    this.sheetBody = this.querySelector('[data-sheet-body]');
    this.sheetTemplate = this.querySelector('[data-sheet-row-template]');
    this.sheetError = this.querySelector('[data-sheet-error]');
    this.sheetDimensionNote = this.querySelector('[data-sheet-dimension-note]');
    this.expeditedNotice = this.querySelector('[data-expedited-notice]');
    this.reviewSummary = this.querySelector('[data-review-summary]');
  }

  bindEvents() {
    this.addEventListener('click', (event) => this.handleClick(event));
    this.addEventListener('change', (event) => this.handleChange(event));
    this.addEventListener('input', (event) => this.handleInput(event));
    this.addEventListener('contextmenu', (event) => this.handleLayerContextMenu(event));
    this.form.addEventListener('submit', (event) => this.handleSubmit(event));

    this.layerList.addEventListener('dragstart', (event) => this.startLayerDrag(event));
    this.layerList.addEventListener('dragover', (event) => this.moveLayerDrag(event));
    this.layerList.addEventListener('drop', (event) => this.endLayerDrag(event));
    this.layerList.addEventListener('dragend', () => this.clearLayerDrag());
    this.layerList.addEventListener('pointerdown', (event) => this.startLayerPointerDrag(event));
    globalThis.addEventListener('pointermove', (event) => this.moveLayerPointerDrag(event));
    globalThis.addEventListener('pointerup', (event) => this.endLayerPointerDrag(event));
    globalThis.addEventListener('pointercancel', (event) => this.endLayerPointerDrag(event));
    globalThis.addEventListener('pointerdown', (event) => {
      if (!this.layerMenu.hidden && !this.layerMenu.contains(event.target)) this.hideLayerMenu();
    });
    globalThis.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.hideLayerMenu();
    });
    globalThis.addEventListener('resize', () => this.hideLayerMenu());

    const dropZone = this.querySelector('[data-drop-zone]');
    ['dragenter', 'dragover'].forEach((name) => {
      dropZone.addEventListener(name, (event) => {
        event.preventDefault();
        dropZone.classList.add('is-dragover');
      });
    });
    ['dragleave', 'drop'].forEach((name) => {
      dropZone.addEventListener(name, (event) => {
        event.preventDefault();
        dropZone.classList.remove('is-dragover');
      });
    });
    dropZone.addEventListener('drop', (event) => this.addArtworkFiles([...event.dataTransfer.files]));
    this.objectLayer.addEventListener('pointerdown', (event) => this.startObjectInteraction(event));
    globalThis.addEventListener('pointermove', (event) => this.moveObject(event));
    globalThis.addEventListener('pointerup', () => this.endObjectInteraction());
  }

  handleClick(event) {
    const contextLayerAction = event.target.closest('[data-context-layer-action]');
    if (contextLayerAction) {
      this.reorderSelectedObject(contextLayerAction.dataset.contextLayerAction);
      this.hideLayerMenu();
      return;
    }

    if (event.target.closest('[data-reset-print-size]')) {
      this.resetRecommendedSize();
      return;
    }

    const rulerUnit = event.target.closest('[data-ruler-unit]');
    if (rulerUnit) {
      this.setRulerUnit(rulerUnit.dataset.rulerUnit);
      return;
    }

    const zoomAction = event.target.closest('[data-zoom-action]');
    if (zoomAction) {
      this.changeZoom(zoomAction.dataset.zoomAction);
      return;
    }

    const serviceLink = event.target.closest('[data-service-link]');
    if (serviceLink) {
      this.openService(serviceLink.dataset.serviceLink);
      return;
    }

    const start = event.target.closest('[data-start]');
    if (start) {
      this.state.mode = start.dataset.start === 'visual' ? 'interactive' : 'quick';
      this.state.expedited = false;
      this.expeditedNotice.hidden = true;
      this.showView(start.dataset.start);
      if (start.dataset.start === 'visual') this.showVisualStep('type');
      return;
    }

    const back = event.target.closest('[data-back]');
    if (back) {
      this.showView(back.dataset.back);
      return;
    }

    const typeButton = event.target.closest('[data-type]');
    if (typeButton) {
      this.selectProductType(typeButton.dataset.type, typeButton.dataset.garmentStyle);
      this.showVisualStep('design');
      return;
    }

    const visualGoto = event.target.closest('[data-visual-goto]');
    if (visualGoto) {
      if (visualGoto.dataset.visualGoto === 'design' && !this.state.type) {
        this.showVisualStep('type');
      } else {
        this.showVisualStep(visualGoto.dataset.visualGoto);
      }
      return;
    }

    const reviewButton = event.target.closest('[data-review-mode]');
    if (reviewButton) {
      this.openReview(reviewButton.dataset.reviewMode);
      return;
    }

    if (event.target.closest('[data-review-back]')) {
      this.showView(this.state.mode === 'quick' ? 'quick' : 'visual');
      return;
    }

    if (event.target.closest('[data-add-text]')) {
      this.addTextObject();
      return;
    }

    if (event.target.closest('[data-delete-object]')) {
      this.deleteSelectedObject();
      return;
    }

    const layerSelect = event.target.closest('[data-layer-select]');
    if (layerSelect) {
      if (this.suppressLayerClick) return;
      this.state.selectedObjectId = layerSelect.dataset.layerSelect;
      this.renderObjects();
      return;
    }

    const layerAction = event.target.closest('[data-layer-action]');
    if (layerAction) {
      this.reorderSelectedObject(layerAction.dataset.layerAction);
      return;
    }

    const rotationAction = event.target.closest('[data-rotation-action]');
    if (rotationAction) {
      const object = this.selectedObject();
      if (!object) return;
      const rotation =
        rotationAction.dataset.rotationAction === 'reset'
          ? 0
          : (Number(object.rotation) || 0) + Number(rotationAction.dataset.rotationAction);
      this.setSelectedRotation(rotation);
      return;
    }

    if (event.target.closest('[data-clear-design]')) {
      this.state.objects = [];
      this.state.selectedObjectId = '';
      this.renderObjects();
      return;
    }

    if (event.target.closest('[data-add-row]')) {
      this.addSheetRow();
      return;
    }

    const removeRow = event.target.closest('[data-remove-row]');
    if (removeRow) {
      if (this.sheetBody.rows.length > 1) removeRow.closest('tr').remove();
      this.renumberSheetRows();
      this.updateSheetDimensionNote();
      return;
    }

    if (event.target.closest('[data-start-over]')) {
      sessionStorage.removeItem('tap:last-mode');
      globalThis.location.href = globalThis.location.pathname;
    }
  }

  handleChange(event) {
    if (event.target === this.materialSelect) {
      this.state.material = event.target.value;
      this.updateBlank();
      this.validateDimensions(false);
      return;
    }
    if (event.target === this.methodSelect) {
      this.state.method = event.target.value;
      return;
    }
    if (event.target === this.garmentSizeSelect) {
      this.changeGarmentSize(event.target.value);
      return;
    }
    if (event.target === this.garmentStyleSelect) {
      this.changeGarmentStyle(event.target.value);
      return;
    }
    if (event.target === this.artworkInput) {
      this.addArtworkFiles([...event.target.files]);
      return;
    }
    if (event.target.matches('[data-text-color]')) {
      const object = this.selectedObject();
      if (object?.type === 'text') {
        object.color = event.target.value;
        this.renderObjects();
      }
      return;
    }
    if (event.target.matches('[data-shoutout-optin]')) {
      const fields = this.querySelector('[data-shoutout-fields]');
      const profile = this.querySelector('[data-social-profile]');
      fields.hidden = !event.target.checked;
      profile.required = event.target.checked;
      return;
    }

    const row = event.target.closest('.tap-sheet-row');
    if (!row) return;
    if (event.target.matches('[data-row-field="type"]')) {
      this.updateSheetRowOptions(row);
      this.updateSheetDimensionNote();
    }
    if (event.target.matches('[data-row-field="garmentStyle"]')) {
      this.updateSheetRowMaterials(row);
    }
    if (event.target.matches('[data-row-field="material"]')) {
      this.updateSheetRowConstraints(row);
      this.updateSheetDimensionNote();
    }
    if (event.target.matches('[data-row-field="artwork"]')) {
      const label = row.querySelector('[data-file-label]');
      label.textContent = event.target.files[0]?.name || 'Choose';
    }
    if (event.target.matches('[data-row-field="artworkStatus"]')) {
      const file = row.querySelector('[data-row-field="artwork"]');
      const wrapper = file.closest('.tap-sheet-file');
      const uploads = event.target.value === 'upload';
      wrapper.hidden = !uploads;
      file.required = uploads;
    }
  }

  handleInput(event) {
    if (event.target.matches('[data-object-rotation]')) {
      this.setSelectedRotation(event.target.value);
      return;
    }
    if ([this.widthInput, this.heightInput].includes(event.target)) {
      if (this.state.type === 'apparel') this.resizeSelectedArtworkFromInputs();
      this.updateBlank();
      this.validateDimensions(false);
      return;
    }
    if (event.target.matches('[data-row-field="width"], [data-row-field="height"]')) {
      this.updateSheetDimensionNote();
    }
  }

  showView(name, updateHistory = true) {
    this.closest('.shopify-section')?.classList.toggle('tap-studio-section--workspace', name !== 'landing');
    this.views.forEach((view) => {
      const active = view.dataset.view === name;
      view.hidden = !active;
      view.classList.toggle('is-active', active);
    });
    if (updateHistory && globalThis.history?.replaceState) {
      const suffix = name === 'landing' ? '' : `#tap-${name}`;
      globalThis.history.replaceState({ tapView: name }, '', `${globalThis.location.pathname}${globalThis.location.search}${suffix}`);
    }
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  showVisualStep(name) {
    this.visualSteps.forEach((step) => {
      const active = step.dataset.visualStep === name;
      step.hidden = !active;
      step.classList.toggle('is-active', active);
    });
    this.stepperButtons.forEach((button) => {
      button.classList.toggle('is-active', button.dataset.visualGoto === name);
    });
  }

  openService(service) {
    if (['quote', 'expedited'].includes(service)) {
      this.state.mode = 'quick';
      this.state.expedited = service === 'expedited';
      this.expeditedNotice.hidden = !this.state.expedited;
      this.showView('quick');
      this.sheetBody.querySelector('[data-row-field="type"]')?.focus({ preventScroll: true });
      return;
    }

    const presets = {
      sticker: { type: 'sticker' },
      poster: { type: 'poster' },
      cup: { type: 'cup' },
      hoodie: { type: 'apparel', garmentStyle: 'hoodie' },
      shirt: { type: 'apparel', garmentStyle: 'shirt' },
    };
    const preset = presets[service];
    if (!preset) return;
    this.state.mode = 'interactive';
    this.state.expedited = false;
    this.showView('visual');
    this.selectProductType(preset.type, preset.garmentStyle);
    this.showVisualStep('design');
  }

  selectProductType(type, garmentStyle = 'shirt') {
    const definition = PRODUCT_TYPES[type];
    if (!definition) return;
    const selectedGarmentStyle = type === 'apparel' && garmentStyle === 'hoodie' ? 'hoodie' : 'shirt';
    this.state.type = type;
    this.state.method = definition.methods[0][0];
    this.state.garmentSize = type === 'apparel' ? 'M' : '';
    this.state.garmentStyle = type === 'apparel' ? selectedGarmentStyle : '';
    this.querySelectorAll('[data-type]').forEach((button) => {
      const sameType = button.dataset.type === type;
      const sameGarment = type !== 'apparel' || button.dataset.garmentStyle === selectedGarmentStyle;
      button.classList.toggle('is-selected', sameType && sameGarment);
    });
    this.querySelector('[data-project-type-label]').textContent =
      type === 'apparel' ? (selectedGarmentStyle === 'hoodie' ? 'Hoodie' : 'Shirt') : definition.label;
    this.querySelector('[data-blank-label]').textContent =
      type === 'apparel' ? this.state.garmentStyle.toUpperCase() : definition.label.toUpperCase();
    this.populateInteractiveMaterials();
    this.methodSelect.innerHTML = definition.methods
      .map(([value, label]) => `<option value="${value}">${label}</option>`)
      .join('');
    this.widthInput.value = definition.width;
    this.heightInput.value = definition.height;
    const isApparel = type === 'apparel';
    this.garmentSizeField.hidden = !isApparel;
    this.garmentSizeSelect.disabled = !isApparel;
    this.garmentSizeSelect.value = isApparel ? this.state.garmentSize : 'M';
    this.garmentStyleField.hidden = !isApparel;
    this.garmentStyleSelect.disabled = !isApparel;
    this.garmentStyleSelect.value = isApparel ? this.state.garmentStyle : 'shirt';
    this.querySelector('[data-width-label]').textContent = isApparel ? 'Print width' : 'Width';
    this.querySelector('[data-height-label]').textContent = isApparel ? 'Print height' : 'Height';
    this.resetPrintSizeButton.textContent = isApparel ? 'Reset to recommended print size' : 'Reset to recommended size';
    this.querySelector('[data-method-field]').hidden = definition.methods.length === 0;
    this.stage.dataset.garmentSize = this.state.garmentSize;
    this.stage.dataset.garmentStyle = this.state.garmentStyle;
    this.state.zoom = 1;
    this.applyCanvasScale();
    this.updateBlank();
  }

  apparelMaterials(style = this.state.garmentStyle) {
    return style === 'hoodie' ? HOODIE_MATERIALS : SHIRT_MATERIALS;
  }

  populateInteractiveMaterials() {
    const definition = PRODUCT_TYPES[this.state.type];
    const materials = this.state.type === 'apparel' ? this.apparelMaterials() : definition.materials;
    this.materialSelect.innerHTML = materials
      .map(([value, label]) => `<option value="${value}">${label}</option>`)
      .join('');
    this.state.material = materials[0]?.[0] || '';
  }

  changeGarmentStyle(style) {
    if (!['shirt', 'hoodie'].includes(style)) return;
    this.state.garmentStyle = style;
    this.stage.dataset.garmentStyle = style;
    this.querySelector('[data-project-type-label]').textContent = style === 'hoodie' ? 'Hoodie' : 'Shirt';
    this.querySelectorAll('[data-type="apparel"]').forEach((button) => {
      button.classList.toggle('is-selected', button.dataset.garmentStyle === style);
    });
    this.querySelector('[data-blank-label]').textContent = style.toUpperCase();
    this.populateInteractiveMaterials();
    this.updateBlank();
  }

  apparelPrintArea(size = this.state.garmentSize) {
    return APPAREL_PRINT_AREAS[size] || APPAREL_PRINT_AREAS.M;
  }

  recommendedPrintSize() {
    if (this.state.type === 'apparel') {
      const dimension = this.state.garmentSize === 'XXL' ? 12 : 11;
      return { width: dimension, height: dimension };
    }
    const definition = PRODUCT_TYPES[this.state.type];
    return { width: Number(definition?.width) || null, height: Number(definition?.height) || null };
  }

  changeGarmentSize(size) {
    if (!GARMENT_SIZES.includes(size)) return;
    const previousArea = this.apparelPrintArea();
    this.state.objects.forEach((object) => {
      if (object.type !== 'image' || object.printWidthIn) return;
      object.printWidthIn = roundDimension((object.width / 100) * previousArea.width);
      object.printHeightIn = roundDimension((object.height / 100) * previousArea.height);
    });
    this.state.garmentSize = size;
    this.stage.dataset.garmentSize = size;
    this.state.objects.forEach((object) => {
      if (object.type === 'image') this.resizeObjectFromPhysicalSize(object);
    });
    if (this.selectedObject()?.type === 'image') this.syncPrintInputsFromObject(this.selectedObject());
    else {
      const recommended = this.recommendedPrintSize();
      this.widthInput.value = recommended.width;
      this.heightInput.value = recommended.height;
    }
    this.applyCanvasScale();
    this.renderRulers();
    this.renderObjects();
  }

  resetRecommendedSize() {
    const recommended = this.recommendedPrintSize();
    if (!recommended.width || !recommended.height) return;
    this.widthInput.value = recommended.width;
    this.heightInput.value = recommended.height;
    const object = this.selectedObject();
    if (this.state.type === 'apparel' && object?.type === 'image') {
      object.printWidthIn = recommended.width;
      object.printHeightIn = recommended.height;
      this.resizeObjectFromPhysicalSize(object, true);
      this.renderObjects();
    }
    this.updateBlank();
    this.validateDimensions(false);
  }

  resizeObjectFromPhysicalSize(object, center = false) {
    if (this.state.type !== 'apparel' || object.type !== 'image') return;
    const area = this.apparelPrintArea();
    object.printWidthIn = roundDimension(clamp(Number(object.printWidthIn) || 1, 1, area.width));
    object.printHeightIn = roundDimension(clamp(Number(object.printHeightIn) || 1, 1, area.height));
    object.width = clamp((object.printWidthIn / area.width) * 100, 8, 100);
    object.height = clamp((object.printHeightIn / area.height) * 100, 6, 100);
    if (center) {
      object.x = (100 - object.width) / 2;
      object.y = Math.max(8, (100 - object.height) / 2);
    } else {
      object.x = clamp(object.x, 0, 100 - object.width);
      object.y = clamp(object.y, 0, 100 - object.height);
    }
  }

  resizeSelectedArtworkFromInputs() {
    const object = this.selectedObject();
    if (this.state.type !== 'apparel' || object?.type !== 'image') return;
    const width = Number(this.widthInput.value);
    const height = Number(this.heightInput.value);
    if (!(width > 0 && height > 0)) return;
    const area = this.apparelPrintArea();
    object.printWidthIn = roundDimension(clamp(width, 1, area.width));
    object.printHeightIn = roundDimension(clamp(height, 1, area.height));
    this.widthInput.value = object.printWidthIn;
    this.heightInput.value = object.printHeightIn;
    this.resizeObjectFromPhysicalSize(object);
    const element = this.objectLayer.querySelector(`[data-object-id="${object.id}"]`);
    if (element) {
      element.style.left = `${object.x}%`;
      element.style.top = `${object.y}%`;
      element.style.width = `${object.width}%`;
      element.style.height = `${object.height}%`;
    }
  }

  syncPrintInputsFromObject(object) {
    if (this.state.type !== 'apparel' || object?.type !== 'image') return;
    const area = this.apparelPrintArea();
    object.printWidthIn = roundDimension((object.width / 100) * area.width);
    object.printHeightIn = roundDimension((object.height / 100) * area.height);
    this.widthInput.value = object.printWidthIn;
    this.heightInput.value = object.printHeightIn;
  }

  rulerDimensions() {
    if (this.state.type === 'apparel') return this.apparelPrintArea();
    return {
      width: Number(this.widthInput.value) || Number(PRODUCT_TYPES[this.state.type]?.width) || 1,
      height: Number(this.heightInput.value) || Number(PRODUCT_TYPES[this.state.type]?.height) || 1,
    };
  }

  setRulerUnit(unit) {
    if (!['imperial', 'metric'].includes(unit)) return;
    this.state.rulerUnit = unit;
    this.querySelectorAll('[data-ruler-unit]').forEach((button) => {
      const active = button.dataset.rulerUnit === unit;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
    this.renderRulers();
  }

  renderRuler(ruler, maximumInches) {
    ruler.replaceChildren();
    const metric = this.state.rulerUnit === 'metric';
    const maximum = metric ? maximumInches * 2.54 : maximumInches;
    const suffix = metric ? 'cm' : 'in';
    for (let index = 0; index <= 4; index += 1) {
      const tick = document.createElement('span');
      tick.className = 'tap-ruler__tick';
      if (index === 0) tick.classList.add('is-first');
      if (index === 4) tick.classList.add('is-last');
      tick.style.setProperty('--tap-ruler-position', `${index * 25}%`);
      tick.textContent = `${roundDimension((maximum * index) / 4)} ${suffix}`;
      ruler.append(tick);
    }
  }

  renderRulers() {
    const dimensions = this.rulerDimensions();
    this.renderRuler(this.rulerX, dimensions.width);
    this.renderRuler(this.rulerY, dimensions.height);
  }

  changeZoom(action) {
    const steps = { out: -0.1, in: 0.1 };
    this.state.zoom = action === 'reset' ? 1 : clamp(this.state.zoom + (steps[action] || 0), 0.5, 1.5);
    this.state.zoom = Math.round(this.state.zoom * 10) / 10;
    this.applyCanvasScale();
  }

  applyCanvasScale() {
    const garmentScale = this.state.type === 'apparel' ? this.apparelPrintArea().visualScale : 1;
    this.surface.style.setProperty('--tap-canvas-scale', this.state.zoom);
    this.surface.style.setProperty('--tap-garment-scale', garmentScale);
    this.zoomOutput.textContent = `${Math.round(this.state.zoom * 100)}%`;
    this.querySelector('[data-zoom-action="out"]').disabled = this.state.zoom <= 0.5;
    this.querySelector('[data-zoom-action="in"]').disabled = this.state.zoom >= 1.5;
  }

  updateBlank() {
    if (!this.state.type) return;
    this.state.material = this.materialSelect.value;
    this.stage.dataset.blankType = this.state.type;
    this.stage.dataset.materialKey = this.state.material;
    const materialLabel = this.materialSelect.selectedOptions[0]?.textContent || 'Material preview';
    this.querySelector('[data-material-label]').textContent = materialLabel;
    const uvTransferSticker = this.state.type === 'sticker' && this.state.material === 'uv';
    if (uvTransferSticker) this.widthInput.max = '11.5';
    else this.widthInput.removeAttribute('max');

    const width = Number(this.widthInput.value);
    const height = Number(this.heightInput.value);
    if (width > 0 && height > 0 && ['banner', 'sticker', 'poster'].includes(this.state.type)) {
      this.surface.style.setProperty('--tap-blank-ratio', clamp(width / height, 0.45, 2.8));
    }
    this.updateDimensionNote();
    this.renderRulers();
  }

  updateDimensionNote() {
    const definition = PRODUCT_TYPES[this.state.type];
    const width = Number(this.widthInput.value);
    const height = Number(this.heightInput.value);
    const rollProduct = ['banner', 'poster'].includes(this.state.type);
    const showRollRule = rollProduct && (width > 52 || height > 52);
    const uvTransferSticker = this.state.type === 'sticker' && this.state.material === 'uv';
    const note = showRollRule ? BANNER_ROLL_NOTE : uvTransferSticker ? UV_TRANSFER_NOTE : definition?.note || '';
    this.dimensionNote.textContent = note;
    this.dimensionNote.hidden = !note;
  }

  validateDimensions(showError = true) {
    this.dimensionError.hidden = true;
    const width = Number(this.widthInput.value);
    const height = Number(this.heightInput.value);
    const rollProduct = ['banner', 'poster'].includes(this.state.type);
    const validRollSize = !rollProduct || (width > 0 && height > 0 && Math.min(width, height) <= 52);
    const validUvWidth = this.state.type !== 'sticker' || this.state.material !== 'uv' || (width > 0 && width <= 11.5);
    const valid = validRollSize && validUvWidth;
    if (!validRollSize && showError) {
      this.dimensionError.textContent = `Rotate or resize this ${this.state.type} so at least one side is 52 inches or less.`;
      this.dimensionError.hidden = false;
    } else if (!validUvWidth && showError) {
      this.dimensionError.textContent = UV_TRANSFER_NOTE;
      this.dimensionError.hidden = false;
    }
    return valid;
  }

  addArtworkFiles(files) {
    const validFiles = files.filter((file) => {
      const isImage = file.type.startsWith('image/');
      const validSize = file.size <= 15 * 1024 * 1024;
      return isImage && validSize;
    });
    if (!validFiles.length) {
      this.dimensionError.textContent = 'Choose an image file no larger than 15MB.';
      this.dimensionError.hidden = false;
      return;
    }

    validFiles.forEach((file) => {
      if (!this.state.files.some((item) => item.name === file.name && item.size === file.size)) {
        this.state.files.push(file);
      }
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        const object = {
          id: createId(),
          type: 'image',
          name: file.name,
          src: reader.result,
          x: 25,
          y: 30,
          width: 50,
          height: 36,
          rotation: 0,
        };
        if (this.state.type === 'apparel') {
          const recommended = this.recommendedPrintSize();
          object.printWidthIn = recommended.width;
          object.printHeightIn = recommended.height;
          this.resizeObjectFromPhysicalSize(object, true);
        }
        this.state.objects.push(object);
        this.state.selectedObjectId = object.id;
        this.renderObjects();
      });
      reader.readAsDataURL(file);
    });
  }

  addTextObject() {
    const input = this.querySelector('[data-new-text]');
    const text = input.value.trim();
    if (!text) {
      input.focus();
      return;
    }
    const object = {
      id: createId(),
      type: 'text',
      text,
      x: 20,
      y: 42,
      width: 60,
      height: 15,
      color: '#171500',
      rotation: 0,
    };
    this.state.objects.push(object);
    this.state.selectedObjectId = object.id;
    input.value = '';
    this.renderObjects();
  }

  selectedObject() {
    return this.state.objects.find((object) => object.id === this.state.selectedObjectId);
  }

  renderObjects() {
    this.objectLayer.replaceChildren();
    this.state.objects.forEach((object, index) => {
      const element = document.createElement('div');
      element.className = `tap-design-object tap-design-object--${object.type}`;
      element.dataset.objectId = object.id;
      element.classList.toggle('is-selected', object.id === this.state.selectedObjectId);
      element.style.left = `${object.x}%`;
      element.style.top = `${object.y}%`;
      element.style.width = `${object.width}%`;
      element.style.height = `${object.height}%`;
      element.style.zIndex = index + 1;
      element.style.setProperty('--tap-object-rotation', `${normalizeRotation(object.rotation)}deg`);
      if (object.type === 'image') {
        const image = document.createElement('img');
        image.src = object.src;
        image.alt = '';
        image.draggable = false;
        element.append(image);
      } else {
        element.textContent = object.text;
        element.style.setProperty('--tap-object-color', object.color);
      }
      const handle = document.createElement('span');
      handle.className = 'tap-resize-handle';
      handle.dataset.resizeHandle = '';
      handle.setAttribute('aria-hidden', 'true');
      element.append(handle);
      this.objectLayer.append(element);
    });
    this.updateInspector();
  }

  updateInspector() {
    const object = this.selectedObject();
    this.inspector.hidden = !object;
    if (!object) return;
    if (object.type === 'image') this.syncPrintInputsFromObject(object);
    this.renderLayerList();
    this.querySelector('[data-selected-label]').textContent = object.type === 'image' ? object.name : object.text;
    const colorField = this.querySelector('[data-text-color-field]');
    colorField.hidden = object.type !== 'text';
    if (object.type === 'text') this.querySelector('[data-text-color]').value = object.color;
    const rotation = normalizeRotation(object.rotation);
    this.querySelector('[data-object-rotation]').value = rotation;
    this.querySelector('[data-rotation-output]').textContent = `${rotation}°`;
  }

  setSelectedRotation(value) {
    const object = this.selectedObject();
    if (!object) return;
    object.rotation = normalizeRotation(value);
    const element = this.objectLayer.querySelector(`[data-object-id="${object.id}"]`);
    element?.style.setProperty('--tap-object-rotation', `${object.rotation}deg`);
    this.querySelector('[data-object-rotation]').value = object.rotation;
    this.querySelector('[data-rotation-output]').textContent = `${object.rotation}°`;
  }

  renderLayerList() {
    const selectedIndex = this.state.objects.findIndex((object) => object.id === this.state.selectedObjectId);
    this.layerList.replaceChildren();
    [...this.state.objects].reverse().forEach((object, visualIndex) => {
      const objectIndex = this.state.objects.length - visualIndex - 1;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'tap-layer-button';
      button.dataset.layerSelect = object.id;
      button.draggable = true;
      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', String(object.id === this.state.selectedObjectId));
      button.setAttribute('aria-label', `${object.type === 'image' ? object.name : object.text}. Drag to reorder or right-click for layer actions.`);
      button.classList.toggle('is-selected', object.id === this.state.selectedObjectId);

      const icon = document.createElement('span');
      icon.className = 'tap-layer-icon';
      icon.textContent = object.type === 'image' ? 'IMG' : 'TXT';
      icon.setAttribute('aria-hidden', 'true');

      const details = document.createElement('span');
      const label = document.createElement('strong');
      label.textContent = object.type === 'image' ? object.name : object.text;
      const position = document.createElement('small');
      position.textContent =
        objectIndex === this.state.objects.length - 1
          ? 'Top artwork'
          : objectIndex === 0
            ? 'Bottom artwork · above blank'
            : `Artwork layer ${objectIndex + 1}`;
      details.append(label, position);

      const order = document.createElement('span');
      order.className = 'tap-layer-grip';
      order.dataset.layerGrip = '';
      order.textContent = '⠿';
      order.title = `Drag artwork layer ${objectIndex + 1}`;
      order.setAttribute('aria-hidden', 'true');
      button.append(icon, details, order);
      this.layerList.append(button);
    });

    this.querySelector('[data-layer-position]').textContent =
      selectedIndex === this.state.objects.length - 1
        ? 'Top artwork'
        : selectedIndex === 0
          ? 'Bottom artwork'
          : `Artwork ${selectedIndex + 1} of ${this.state.objects.length}`;
    this.querySelectorAll('[data-layer-action]').forEach((button) => {
      const movesDown = ['bottom', 'backward'].includes(button.dataset.layerAction);
      button.disabled = movesDown ? selectedIndex <= 0 : selectedIndex >= this.state.objects.length - 1;
    });
  }

  startLayerDrag(event) {
    const button = event.target.closest('[data-layer-select]');
    if (!button || !event.dataTransfer) return;
    this.draggedLayerId = button.dataset.layerSelect;
    this.state.selectedObjectId = this.draggedLayerId;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/x-tap-layer', this.draggedLayerId);
    event.dataTransfer.setData('text/plain', this.draggedLayerId);
    button.classList.add('is-dragging');
  }

  moveLayerDrag(event) {
    if (!this.draggedLayerId) return;
    const target = event.target.closest('[data-layer-select]');
    if (!target || target.dataset.layerSelect === this.draggedLayerId) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    this.layerList.querySelectorAll('.is-drop-target').forEach((button) => button.classList.remove('is-drop-target'));
    target.classList.add('is-drop-target');
  }

  endLayerDrag(event) {
    if (!this.draggedLayerId) return;
    const target = event.target.closest('[data-layer-select]');
    if (!target || target.dataset.layerSelect === this.draggedLayerId) {
      this.clearLayerDrag();
      return;
    }
    event.preventDefault();
    this.moveLayerToTarget(this.draggedLayerId, target.dataset.layerSelect);
    this.clearLayerDrag();
  }

  clearLayerDrag() {
    this.layerList.querySelectorAll('.is-dragging, .is-drop-target').forEach((button) => {
      button.classList.remove('is-dragging', 'is-drop-target');
    });
    this.draggedLayerId = '';
  }

  startLayerPointerDrag(event) {
    if (event.pointerType === 'mouse' || !event.target.closest('[data-layer-grip]')) return;
    const button = event.target.closest('[data-layer-select]');
    if (!button) return;
    this.layerPointerDrag = {
      pointerId: event.pointerId,
      sourceId: button.dataset.layerSelect,
      targetId: '',
      startX: event.clientX,
      startY: event.clientY,
      active: false,
    };
  }

  moveLayerPointerDrag(event) {
    const drag = this.layerPointerDrag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    const distance = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY);
    if (!drag.active && distance < 8) return;
    drag.active = true;
    event.preventDefault();
    const source = this.layerList.querySelector(`[data-layer-select="${drag.sourceId}"]`);
    source?.classList.add('is-dragging');
    const target = document.elementFromPoint(event.clientX, event.clientY)?.closest('[data-layer-select]');
    const validTarget = target && this.layerList.contains(target) && target.dataset.layerSelect !== drag.sourceId;
    drag.targetId = validTarget ? target.dataset.layerSelect : '';
    this.layerList.querySelectorAll('.is-drop-target').forEach((button) => button.classList.remove('is-drop-target'));
    if (validTarget) target.classList.add('is-drop-target');
  }

  endLayerPointerDrag(event) {
    const drag = this.layerPointerDrag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    if (drag.active) {
      event.preventDefault();
      this.suppressLayerClick = true;
      if (drag.targetId) this.moveLayerToTarget(drag.sourceId, drag.targetId);
      globalThis.setTimeout(() => {
        this.suppressLayerClick = false;
      });
    }
    this.layerList.querySelectorAll('.is-dragging, .is-drop-target').forEach((button) => {
      button.classList.remove('is-dragging', 'is-drop-target');
    });
    this.layerPointerDrag = null;
  }

  moveLayerToTarget(sourceId, targetId) {
    const sourceIndex = this.state.objects.findIndex((object) => object.id === sourceId);
    const targetIndex = this.state.objects.findIndex((object) => object.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) return;
    const [object] = this.state.objects.splice(sourceIndex, 1);
    this.state.objects.splice(Math.min(targetIndex, this.state.objects.length), 0, object);
    this.state.selectedObjectId = sourceId;
    this.renderObjects();
  }

  handleLayerContextMenu(event) {
    const layerButton = event.target.closest('[data-layer-select]');
    const stageObject = event.target.closest('[data-object-id]');
    const objectId = layerButton?.dataset.layerSelect || stageObject?.dataset.objectId;
    if (!objectId) return;
    event.preventDefault();
    this.state.selectedObjectId = objectId;
    this.renderObjects();

    const selectedIndex = this.state.objects.findIndex((object) => object.id === objectId);
    this.layerMenu.querySelector('[data-context-layer-action="backward"]').disabled = selectedIndex <= 0;
    this.layerMenu.querySelector('[data-context-layer-action="forward"]').disabled =
      selectedIndex >= this.state.objects.length - 1;
    this.layerMenu.hidden = false;
    const rect = this.layerMenu.getBoundingClientRect();
    const left = clamp(event.clientX, 8, Math.max(8, globalThis.innerWidth - rect.width - 8));
    const top = clamp(event.clientY, 8, Math.max(8, globalThis.innerHeight - rect.height - 8));
    this.layerMenu.style.left = `${left}px`;
    this.layerMenu.style.top = `${top}px`;
    this.layerMenu.querySelector('button:not(:disabled)')?.focus({ preventScroll: true });
  }

  hideLayerMenu() {
    this.layerMenu.hidden = true;
  }

  reorderSelectedObject(action) {
    const currentIndex = this.state.objects.findIndex((object) => object.id === this.state.selectedObjectId);
    if (currentIndex < 0) return;
    const lastIndex = this.state.objects.length - 1;
    const targets = {
      bottom: 0,
      backward: Math.max(0, currentIndex - 1),
      forward: Math.min(lastIndex, currentIndex + 1),
      top: lastIndex,
    };
    const targetIndex = targets[action];
    if (targetIndex === undefined || targetIndex === currentIndex) return;
    const [object] = this.state.objects.splice(currentIndex, 1);
    this.state.objects.splice(targetIndex, 0, object);
    this.renderObjects();
  }

  deleteSelectedObject() {
    this.state.objects = this.state.objects.filter((object) => object.id !== this.state.selectedObjectId);
    this.state.selectedObjectId = '';
    this.renderObjects();
  }

  startObjectInteraction(event) {
    if (event.button !== 0) return;
    const element = event.target.closest('[data-object-id]');
    if (!element) return;
    const object = this.state.objects.find((item) => item.id === element.dataset.objectId);
    if (!object) return;
    event.preventDefault();
    this.state.selectedObjectId = object.id;
    this.renderObjects();
    this.dragState = {
      mode: event.target.matches('[data-resize-handle]') ? 'resize' : 'move',
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      object,
      original: { x: object.x, y: object.y, width: object.width, height: object.height },
      rect: this.objectLayer.getBoundingClientRect(),
    };
  }

  moveObject(event) {
    if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;
    const { object, original, rect, mode } = this.dragState;
    const screenDx = event.clientX - this.dragState.startX;
    const screenDy = event.clientY - this.dragState.startY;
    let dx = (screenDx / rect.width) * 100;
    let dy = (screenDy / rect.height) * 100;
    if (mode === 'move') {
      object.x = clamp(original.x + dx, 0, 100 - object.width);
      object.y = clamp(original.y + dy, 0, 100 - object.height);
    } else {
      const radians = (normalizeRotation(object.rotation) * Math.PI) / 180;
      const localDx = screenDx * Math.cos(radians) + screenDy * Math.sin(radians);
      const localDy = -screenDx * Math.sin(radians) + screenDy * Math.cos(radians);
      dx = (localDx / rect.width) * 100;
      dy = (localDy / rect.height) * 100;
      object.width = clamp(original.width + dx, 8, 100 - object.x);
      object.height = clamp(original.height + dy, 6, 100 - object.y);
      if (object.type === 'image') this.syncPrintInputsFromObject(object);
    }
    const element = this.objectLayer.querySelector(`[data-object-id="${object.id}"]`);
    if (element) {
      element.style.left = `${object.x}%`;
      element.style.top = `${object.y}%`;
      element.style.width = `${object.width}%`;
      element.style.height = `${object.height}%`;
    }
  }

  endObjectInteraction() {
    this.dragState = null;
  }

  addSheetRow() {
    const row = this.sheetTemplate.content.firstElementChild.cloneNode(true);
    this.sheetBody.append(row);
    this.renumberSheetRows();
    this.updateSheetDimensionNote();
    row.querySelector('[data-row-field="type"]').focus({ preventScroll: true });
  }

  renumberSheetRows() {
    [...this.sheetBody.rows].forEach((row, index) => {
      row.querySelector('[data-line-number]').textContent = index + 1;
    });
    const count = this.sheetBody.rows.length;
    this.querySelector('[data-sheet-count]').textContent = `${count} ${count === 1 ? 'line' : 'lines'}`;
  }

  updateSheetRowOptions(row) {
    const type = row.querySelector('[data-row-field="type"]').value;
    const definition = PRODUCT_TYPES[type];
    const populate = (field, options) => {
      const select = row.querySelector(`[data-row-field="${field}"]`);
      select.innerHTML = options?.length
        ? options.map(([value, label]) => `<option value="${value}">${label}</option>`).join('')
        : '<option value="">Select type first</option>';
    };
    populate('method', definition?.methods);
    const garmentStyle = row.querySelector('[data-row-field="garmentStyle"]');
    const garmentSize = row.querySelector('[data-row-field="garmentSize"]');
    const isApparel = type === 'apparel';
    garmentStyle.disabled = !isApparel;
    garmentStyle.innerHTML = isApparel
      ? '<option value="shirt">Shirt</option><option value="hoodie">Hoodie</option>'
      : '<option value="">—</option>';
    garmentSize.disabled = !isApparel;
    garmentSize.innerHTML = isApparel
      ? GARMENT_SIZES.map((size) => `<option value="${size}"${size === 'M' ? ' selected' : ''}>${size}</option>`).join('')
      : '<option value="">—</option>';
    if (isApparel) {
      row.querySelector('[data-row-field="width"]').value ||= PRODUCT_TYPES.apparel.width;
      row.querySelector('[data-row-field="height"]').value ||= PRODUCT_TYPES.apparel.height;
    }
    this.updateSheetRowMaterials(row);
    this.updateSheetRowConstraints(row);
  }

  updateSheetRowMaterials(row) {
    const type = row.querySelector('[data-row-field="type"]').value;
    const select = row.querySelector('[data-row-field="material"]');
    const garmentStyle = row.querySelector('[data-row-field="garmentStyle"]').value;
    const materials = type === 'apparel'
      ? this.apparelMaterials(garmentStyle || 'shirt')
      : PRODUCT_TYPES[type]?.materials;
    select.innerHTML = materials?.length
      ? materials.map(([value, label]) => `<option value="${value}">${label}</option>`).join('')
      : '<option value="">Select type first</option>';
    this.updateSheetRowConstraints(row);
  }

  updateSheetRowConstraints(row) {
    const type = row.querySelector('[data-row-field="type"]').value;
    const material = row.querySelector('[data-row-field="material"]').value;
    const width = row.querySelector('[data-row-field="width"]');
    if (type === 'sticker' && material === 'uv') width.max = '11.5';
    else width.removeAttribute('max');
  }

  updateSheetDimensionNote() {
    const rows = [...this.sheetBody.rows];
    const showRollRule = rows.some((row) => {
      const type = row.querySelector('[data-row-field="type"]').value;
      const width = Number(row.querySelector('[data-row-field="width"]').value);
      const height = Number(row.querySelector('[data-row-field="height"]').value);
      return ['banner', 'poster'].includes(type) && (width > 52 || height > 52);
    });
    const showUvRule = rows.some((row) => {
      const type = row.querySelector('[data-row-field="type"]').value;
      const material = row.querySelector('[data-row-field="material"]').value;
      return type === 'sticker' && material === 'uv';
    });
    const note = showRollRule ? BANNER_ROLL_NOTE : showUvRule ? UV_TRANSFER_NOTE : '';
    this.sheetDimensionNote.textContent = note;
    this.sheetDimensionNote.hidden = !note;
  }

  collectSheetItems(validate = false) {
    const rows = [...this.sheetBody.rows];
    const items = [];
    let error = '';
    rows.forEach((row, index) => {
      const value = (field) => row.querySelector(`[data-row-field="${field}"]`)?.value.trim() || '';
      const type = value('type');
      const width = Number(value('width')) || null;
      const height = Number(value('height')) || null;
      const quantity = Number(value('quantity')) || 0;
      const artworkInput = row.querySelector('[data-row-field="artwork"]');
      const artworkStatus = value('artworkStatus');
      const methodSelect = row.querySelector('[data-row-field="method"]');
      const garmentSize = value('garmentSize');
      const garmentStyle = value('garmentStyle');
      if (validate && !type) error ||= `Choose a type on line ${index + 1}.`;
      if (validate && type && !value('method')) error ||= `Choose a print method on line ${index + 1}.`;
      if (validate && type === 'apparel' && !garmentSize) error ||= `Choose a garment size on line ${index + 1}.`;
      if (validate && type === 'apparel' && !garmentStyle) error ||= `Choose a garment on line ${index + 1}.`;
      if (validate && quantity < 1) error ||= `Enter a quantity on line ${index + 1}.`;
      if (validate && ['banner', 'poster'].includes(type) && (!width || !height || Math.min(width, height) > 52)) {
        error ||= `${PRODUCT_TYPES[type].label} line ${index + 1} needs at least one side at 52 inches or less.`;
      }
      if (validate && type === 'sticker' && value('material') === 'uv' && (!width || width > 11.5)) {
        error ||= `UV transfer sticker line ${index + 1} has a maximum width of 11.5 inches.`;
      }
      if (validate && artworkStatus === 'upload' && !artworkInput.files.length) {
        error ||= `Choose artwork or change the artwork status on line ${index + 1}.`;
      }
      items.push({
        clientLineId: row.dataset.lineId || (row.dataset.lineId = createId()),
        type,
        typeLabel: PRODUCT_TYPES[type]?.label || type,
        material: value('material'),
        method: value('method'),
        methodLabel: methodSelect.selectedOptions[0]?.textContent || '',
        garmentSize: type === 'apparel' ? garmentSize : '',
        garmentStyle: type === 'apparel' ? garmentStyle : '',
        dimensions: { widthIn: width, heightIn: height },
        quantity,
        artworkStatus,
        artwork: [...artworkInput.files].map((file) => ({
          name: file.name,
          type: file.type,
          size: file.size,
        })),
        notes: value('notes'),
      });
    });
    this.sheetError.textContent = error;
    this.sheetError.hidden = !error;
    return { items, valid: !error };
  }

  openReview(mode) {
    this.state.mode = mode;
    if (mode === 'interactive') {
      if (!this.state.type) {
        this.showView('visual');
        this.showVisualStep('type');
        return;
      }
      if (!this.validateDimensions(true)) return;
      if (!this.state.objects.length) {
        this.dimensionError.textContent = 'Add artwork or create a text layer before requesting a quote.';
        this.dimensionError.hidden = false;
        this.showView('visual');
        this.showVisualStep('design');
        return;
      }
    } else {
      const sheet = this.collectSheetItems(true);
      if (!sheet.valid) return;
    }
    this.updatePayload();
    this.renderReview();
    this.showView('review');
  }

  buildPayload() {
    let items;
    if (this.state.mode === 'quick') {
      items = this.collectSheetItems(false).items;
    } else {
      const definition = PRODUCT_TYPES[this.state.type];
      items = [
        {
          clientLineId: createId(),
          type: this.state.type,
          typeLabel: definition?.label || this.state.type,
          material: this.state.material,
          method: this.state.method,
          methodLabel: this.methodSelect.selectedOptions[0]?.textContent || this.state.method,
          garmentSize: this.state.type === 'apparel' ? this.state.garmentSize : '',
          garmentStyle: this.state.type === 'apparel' ? this.state.garmentStyle : '',
          dimensions: {
            widthIn: Number(this.widthInput.value) || null,
            heightIn: Number(this.heightInput.value) || null,
          },
          quantity: Number(this.quantityInput.value) || 1,
          artworkStatus: 'configured-in-studio',
          artwork: this.state.files.map((file) => ({
            name: file.name,
            type: file.type,
            size: file.size,
          })),
          design: {
            blankType: this.state.type,
            objects: this.state.objects.map(({ src, ...object }, index) => ({
              ...object,
              stackOrder: index + 1,
            })),
          },
        },
      ];
    }

    const optIn = this.querySelector('[data-shoutout-optin]')?.checked || false;
    const platform = this.querySelector('[data-social-platform]')?.value || '';
    const profileInput = this.querySelector('[data-social-profile]')?.value.trim() || '';
    return {
      schemaVersion: TAP_PROJECT_VERSION,
      submissionId: this.state.submissionId,
      createdAt: new Date().toISOString(),
      source: {
        channel: 'shopify-theme',
        shopDomain: this.dataset.shopDomain,
        sectionId: this.dataset.sectionId,
      },
      mode: this.state.mode,
      serviceLevel: this.state.expedited ? 'expedited' : 'standard',
      customer: {
        name: this.form.elements['contact[name]']?.value || '',
        company: this.form.elements['contact[Company]']?.value || '',
        email: this.form.elements['contact[email]']?.value || '',
        phone: this.form.elements['contact[phone]']?.value || '',
      },
      socialShoutout: {
        optIn,
        platform: optIn ? platform : '',
        providedProfile: optIn ? profileInput : '',
        normalizedUrl: optIn ? this.normalizeSocialUrl(platform, profileInput) : '',
      },
      items,
      integration: {
        requestedAction: 'quote',
        orderCreationReady: false,
      },
    };
  }

  updatePayload() {
    const payload = this.buildPayload();
    const summary = this.formatPlainSummary(payload);
    const notificationDetails = [];
    if (this.nativeNotificationReference) {
      notificationDetails.push(`Quote reference: ${this.nativeNotificationReference}`);
    }
    if (this.nativeNotificationArtwork.length) {
      notificationDetails.push(
        ['Customer-uploaded artwork:', ...this.nativeNotificationArtwork.map((file) => `${file.name}: ${file.url}`)].join('\n'),
      );
    }
    this.querySelector('[data-payload-mode]').value = payload.mode;
    this.querySelector('[data-payload-json]').value = JSON.stringify(payload);
    this.querySelector('[data-payload-summary]').value = [...notificationDetails, summary].join('\n\n');
    sessionStorage.setItem('tap:last-mode', payload.mode);
    return payload;
  }

  collectNativeArtworkLinks(result) {
    if (!Array.isArray(result?.artwork)) return [];
    const seen = new Set();
    return result.artwork.flatMap((file, index) => {
      try {
        const url = new URL(String(file?.url || ''));
        if (url.protocol !== 'https:' || url.hostname !== 'cdn.shopify.com' || seen.has(url.href)) return [];
        seen.add(url.href);
        const name = String(file?.name || `Artwork ${index + 1}`)
          .replace(/[\r\n]+/g, ' ')
          .trim()
          .slice(0, 200);
        return [{ name: name || `Artwork ${index + 1}`, url: url.href }];
      } catch {
        return [];
      }
    });
  }

  formatPlainSummary(payload) {
    const lines = [
      `TAP project request ${payload.submissionId}`,
      `Path: ${payload.mode}`,
      `Service: ${payload.serviceLevel}`,
      '',
    ];
    payload.items.forEach((item, index) => {
      const dimensions =
        item.dimensions.widthIn && item.dimensions.heightIn
          ? `${item.dimensions.widthIn}" × ${item.dimensions.heightIn}"`
          : 'dimensions to confirm';
      lines.push(
        `${index + 1}. ${item.typeLabel || item.type} — ${item.material || 'material to confirm'} — ${dimensions} — qty ${item.quantity}`,
      );
      if (item.garmentSize) lines.push(`   Garment size: ${item.garmentSize}`);
      if (item.garmentStyle) lines.push(`   Garment: ${item.garmentStyle}`);
      if (item.method) lines.push(`   Method: ${item.methodLabel || item.method}`);
      if (item.artworkStatus) lines.push(`   Artwork: ${item.artworkStatus}`);
      if (item.artwork?.length) lines.push(`   Files: ${item.artwork.map((file) => file.name).join(', ')}`);
      if (item.design?.objects?.length) lines.push(`   Design layers: ${item.design.objects.length}`);
      if (item.notes) lines.push(`   Notes: ${item.notes}`);
    });
    return lines.join('\n');
  }

  renderReview() {
    const payload = this.buildPayload();
    this.reviewSummary.replaceChildren();
    payload.items.forEach((item, index) => {
      const line = document.createElement('article');
      line.className = 'tap-summary-line';
      const number = document.createElement('span');
      number.className = 'tap-summary-line__number';
      number.textContent = index + 1;
      const content = document.createElement('div');
      const title = document.createElement('strong');
      title.textContent = item.typeLabel || item.type;
      const details = document.createElement('small');
      const dimensions =
        item.dimensions.widthIn && item.dimensions.heightIn
          ? `${item.dimensions.widthIn}" × ${item.dimensions.heightIn}`
          : 'Size to confirm';
      const material = PRODUCT_TYPES[item.type]?.materials.find(([value]) => value === item.material)?.[1] || item.material;
      const method = item.methodLabel || PRODUCT_TYPES[item.type]?.methods.find(([value]) => value === item.method)?.[1] || item.method;
      const garmentSize = item.garmentSize ? ` · Size ${item.garmentSize}` : '';
      details.textContent = `${material || 'Material to confirm'} · ${method || 'Method to confirm'}${garmentSize} · ${dimensions} · Qty ${item.quantity}`;
      content.append(title, details);
      if (item.artwork?.length || item.design?.objects?.length) {
        const art = document.createElement('small');
        art.textContent = item.artwork?.length
          ? `Artwork: ${item.artwork.map((file) => file.name).join(', ')}`
          : `${item.design.objects.length} design layer${item.design.objects.length === 1 ? '' : 's'}`;
        content.append(art);
      }
      line.append(number, content);
      this.reviewSummary.append(line);
    });
    this.querySelector('[data-review-mode-label]').textContent =
      this.state.expedited
        ? 'Expedited · Quick sheet'
        : this.state.mode === 'quick'
          ? 'Quick sheet'
          : 'Interactive build';
    const hasFiles = payload.items.some((item) => item.artwork?.length);
    this.querySelector('[data-browser-file-note]').hidden = Boolean(this.apiEndpoint) || !hasFiles;
  }

  normalizeSocialUrl(platform, rawProfile) {
    const raw = rawProfile.trim();
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw)) return raw;
    const handle = raw.replace(/^@/, '').replace(/^\/+|\/+$/g, '');
    const bases = {
      Instagram: 'https://instagram.com/',
      Facebook: 'https://facebook.com/',
      TikTok: 'https://tiktok.com/@',
      X: 'https://x.com/',
    };
    return `${bases[platform] || ''}${handle}`;
  }

  async handleSubmit(event) {
    this.updatePayload();
    if (!this.apiEndpoint) return;
    event.preventDefault();
    if (!this.form.reportValidity()) return;
    const submit = this.querySelector('[data-submit]');
    const status = this.querySelector('[data-submit-status]');
    submit.disabled = true;
    status.textContent = 'Sending project…';
    try {
      const body = new FormData(this.form);
      const existingFiles = new Set(
        body
          .getAll('artwork_files[]')
          .filter((value) => value instanceof File && value.size > 0)
          .map((file) => `${file.name}:${file.size}`),
      );
      this.state.files.forEach((file) => {
        const signature = `${file.name}:${file.size}`;
        if (!existingFiles.has(signature)) body.append('artwork_files[]', file, file.name);
      });
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        body,
        headers: { Accept: 'application/json' },
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error?.message || `Request failed (${response.status})`);
      }
      if (result.redirectUrl) {
        globalThis.location.assign(result.redirectUrl);
        return;
      }
      if (!result.notificationSent && !result.duplicate) {
        this.submitMerchantNotification(result);
        return;
      }
      this.showApiSuccess(result);
    } catch (error) {
      status.textContent = `${error.message}. Your project is still here—please try again.`;
      submit.disabled = false;
    }
  }

  submitMerchantNotification(result) {
    this.nativeNotificationReference = result.quoteId || result.submissionId || 'Saved quote';
    this.nativeNotificationArtwork = this.collectNativeArtworkLinks(result);
    this.apiEndpoint = '';
    this.querySelector('[data-submit-status]').textContent =
      'Request saved. Sending the notification…';
    this.updatePayload();
    const projectPayload = this.querySelector('[data-payload-json]');
    projectPayload.disabled = true;
    projectPayload.removeAttribute('name');
    this.querySelectorAll('input[type="file"]').forEach((input) => {
      input.disabled = true;
      input.removeAttribute('name');
    });
    this.form.requestSubmit();
  }

  showApiSuccess(result) {
    const grid = this.querySelector('.tap-review-grid');
    if (grid) {
      const success = document.createElement('div');
      success.className = 'tap-success';
      success.tabIndex = -1;

      const kicker = document.createElement('p');
      kicker.className = 'tap-kicker';
      kicker.textContent = 'Request received';

      const heading = document.createElement('h3');
      heading.textContent = 'We’ve got the specs.';

      const message = document.createElement('p');
      message.textContent = `${result.quoteId ? `Reference ${result.quoteId}. ` : ''}Our team will follow up with pricing and production questions.`;

      const button = document.createElement('button');
      button.className = 'tap-button tap-button--ink';
      button.type = 'button';
      button.dataset.startOver = '';
      button.textContent = 'Start another project';

      success.append(kicker, heading, message, button);
      grid.replaceChildren(success);
      success.focus();
    }
  }
}

if (!customElements.get('tap-studio')) {
  customElements.define('tap-studio', TapStudio);
}
